import { ContactAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';

/**
 * One enrolled contact positioned on the touch graph — the fields a face pile
 * needs to render an avatar. Shared by the canvas overlay and the pile itself.
 */
export interface TouchFlowContact {
  cadenceId: string;
  contactId: string;
  firstName: string | null;
  lastName: string | null;
  imageSrc: string | null;
  orgImageSrc: string | null;
}

/** How many faces to show before collapsing the rest into a "+N" chip. */
const MAX_FACES = 3;
/** How many names to list in the hover tooltip before summarizing the rest as "+N more". */
const MAX_TOOLTIP_NAMES = 12;

const contactName = (c: TouchFlowContact) => [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unknown';

/**
 * A compact horizontal row of overlapping contact avatars, sized to tuck into the gap
 * beside a touch node. It grows left → right (never down over the graph below). Shows up
 * to `MAX_FACES` faces then a "+N" overflow chip; the whole pile carries a tooltip
 * listing who's here.
 */
export function ContactFacePile({ contacts }: { contacts: TouchFlowContact[] }) {
  if (contacts.length === 0) return null;

  const shown = contacts.slice(0, MAX_FACES);
  const overflow = contacts.length - shown.length;
  const names = contacts.slice(0, MAX_TOOLTIP_NAMES).map(contactName).join(', ');
  const label =
    `${contacts.length} contact${contacts.length === 1 ? '' : 's'} here: ${names}` +
    (contacts.length > MAX_TOOLTIP_NAMES ? `, +${contacts.length - MAX_TOOLTIP_NAMES} more` : '');

  return (
    <Tooltip tooltip={label} className="flex items-center">
      <div data-testid="touch-contact-facepile" className="flex items-center">
        {shown.map((c, i) => (
          <div key={c.cadenceId} className={cn('rounded-full ring-2 ring-white dark:ring-slate-900', i > 0 && '-ml-2')}>
            <ContactAvatar
              firstName={c.firstName}
              lastName={c.lastName}
              imageSrc={c.imageSrc}
              orgImageSrc={c.orgImageSrc}
              className="size-7"
            />
          </div>
        ))}
        {overflow > 0 && (
          <div
            data-testid="touch-contact-facepile-overflow"
            className="-ml-2 flex size-7 items-center justify-center rounded-full bg-lynch-100 text-[10px] font-semibold text-lynch-600 ring-2 ring-white dark:bg-slate-700 dark:text-lynch-200 dark:ring-slate-900">
            +{overflow}
          </div>
        )}
      </div>
    </Tooltip>
  );
}
